import type {
  DBServerRecord,
  Configuration,
  QueryDBRestoreDestination,
} from '@repo/api-gw-sdk';
import {
  GetDatabasesByServerApi,
  GetSnapshotsByServerApi,
  DatabaseSnapshotsApi,
  retry,
} from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/db';

export const db = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const snapshotsApi = new GetSnapshotsByServerApi(config);
  const getDbApi = new GetDatabasesByServerApi(config);
  const databaseSnapshotsApi = new DatabaseSnapshotsApi(config);

  return {
    snapshots: (serverId: string, startDate: Date, endDate: Date) =>
      httpClient.execute(
        snapshotsApi.getSnapshotsByServer.bind(snapshotsApi),
        baseUrl,
        '',
        serverId,
        startDate,
        endDate,
        currentProjectId
      ),
    objectTreeMultiple: (serverIds: string[], snapshotId: string) => {
      return httpClient.execute(
        (serverIds: string[], snapshotId: string, projectId: string) =>
          !snapshotId
            ? Promise.resolve(undefined)
            : Promise.all(
                serverIds.map((serverId) =>
                  getDbApi.getDatabasesByServer(serverId, snapshotId, projectId)
                )
              ).then((results) => {
                const distinctResults: DBServerRecord[] = [];
                for (const y of results) {
                  if (!distinctResults.some((x) => x.serverId === y.serverId)) {
                    distinctResults.push(y);
                  }
                }

                return distinctResults;
              }),
        baseUrl,
        '',
        serverIds,
        snapshotId,
        currentProjectId
      );
    },
    querySnapshot: (
      snapshotId: string,
      databaseName: string,
      query: string,
      targetAccountId: string,
      destination: QueryDBRestoreDestination,
      signal?: AbortSignal
    ) => {
      if (!snapshotId) {
        return Promise.resolve(undefined);
      }

      return retry(
        () =>
          databaseSnapshotsApi.runQuery(
            currentProjectId,
            snapshotId,
            {
              restoreAccountId: targetAccountId,
              databaseName,
              query,
              destination,
            },
            {
              signal,
            }
          ),
        3 // Picked by Peleg as his lucky number
      );
    },
    queryStatus: (queryId: string) =>
      databaseSnapshotsApi.getQueryStatus(currentProjectId, queryId),
    queryResultsPromise: (queryId: string) =>
      databaseSnapshotsApi.getQueryResult(currentProjectId, queryId),
    queryResults: (queryId: string) =>
      httpClient.execute(
        databaseSnapshotsApi.getQueryResult.bind(databaseSnapshotsApi),
        baseUrl,
        '',
        currentProjectId,
        queryId
      ),
    generateQuery: (text: string) =>
      httpClient.post('/projects/{projectId}/db/generate-query', { text }),
  };
};
